import { Component, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { firstValueFrom, Subject, takeUntil } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import {
  Currency,
  EDocumentTypeNature,
  EPurchaseOrderLineState,
  HomePurchaseOrderLine,
  ILevelBreadcrumbLevel,
  LevelBreadcrumbSelectionMap,
  PurchaseOrderLineStates,
  TId,
} from 'src/app/models';
import { PaginatedCardListComponent } from 'src/app/libs/components/paginated-card-list/paginated-card-list.component';
import { TabRole, TabsService } from 'src/app/libs/components/tabs-host/tabs.service';
import { IListableResource } from 'src/app/resources';
import { OmsPurchaseOrdersEditComponent } from 'src/app/routes/oms/purchases/oms-purchase-orders/oms-purchase-orders-edit/oms-purchase-orders-edit.component';
import { MasterApiService } from 'src/app/services/master-api.service';
import { NatureStateService } from 'src/app/services/nature-state.service';

@Component({
  selector: 'app-admin-home-purchases',
  templateUrl: './admin-home-purchases.component.html',
  styleUrls: ['./admin-home-purchases.component.sass'],
  standalone: false,
})
export class AdminHomePurchasesComponent implements OnInit, OnDestroy {
  private readonly destroy$ = new Subject<void>();
  @ViewChild('cardList') cardList?: PaginatedCardListComponent<HomePurchaseOrderLine>;
  @ViewChild('projectItemTpl') projectItemTpl?: TemplateRef<unknown>;
  @ViewChild('projectFieldTpl') projectFieldTpl?: TemplateRef<unknown>;
  @ViewChild('budgetDistItemTpl') budgetDistItemTpl?: TemplateRef<unknown>;
  @ViewChild('budgetDistFieldTpl') budgetDistFieldTpl?: TemplateRef<unknown>;

  get levelItemTemplates(): Record<string, TemplateRef<unknown>> {
    return {
      ...(this.projectItemTpl ? { projectId: this.projectItemTpl } : {}),
      ...(this.budgetDistItemTpl ? { projectBudgetDistributionId: this.budgetDistItemTpl } : {}),
    };
  }

  get levelFieldTemplates(): Record<string, TemplateRef<unknown>> {
    return {
      ...(this.projectFieldTpl ? { projectId: this.projectFieldTpl } : {}),
      ...(this.budgetDistFieldTpl ? { projectBudgetDistributionId: this.budgetDistFieldTpl } : {}),
    };
  }

  form?: FormGroup;

  breadcrumbLevels: ILevelBreadcrumbLevel[] = [];

  levelParams: LevelBreadcrumbSelectionMap = {};

  private readonly projectsResource = this.api.resources.operations.projects;

  productsResource = this.api.resources.product.products;
  providersResource = this.api.resources.organization.suppliers;

  homePurchaseOrderLinesResource: IListableResource<HomePurchaseOrderLine> =
    this.api.resources.operations.homePurchaseOrderLines;

  orderStates: { id: TId; name: string }[] = [];
  lineStates: { id: EPurchaseOrderLineState; name: string }[] = [];
  currenciesById: Record<string, Currency> = {};
  langCode = this.api.auth.userValue?.languageCode ?? this.tr.currentLang;

  breadCrumbVisible: boolean = true;

  
  constructor(
    private api: MasterApiService,
    private fb: FormBuilder,
    private tr: TranslateService,
    private tabs: TabsService
  ) {}

  get listQueryParams(): Record<string, string> {
    return this.buildListParams();
  }

  async ngOnInit(): Promise<void> {
    this.form = this.fb.group({
      purchaseOrder: [undefined],
      deliveryNote: [undefined],
      startDate: [undefined],
      endDate: [undefined],
      productId: [undefined],
      orderStates: [[]],
      lineStates: [[]],
      startDeliveryDate: [undefined],
      endDeliveryDate: [undefined],
      providerId: [undefined],
    });

    this.form.valueChanges
      .pipe(debounceTime(1000), takeUntil(this.destroy$))
      .subscribe(() => this.refreshList());

    this.orderStates = [];
    this.loadOrderStates();
    void this.loadCurrencies();
    this.lineStates = PurchaseOrderLineStates.map(state => ({
      id: state.id,
      name: this.tr.instant(
        `enumeration.PurchaseOrderLineState.${EPurchaseOrderLineState[state.id]}`
      ),
    }));

    this.breadcrumbLevels = [
      {
        paramKey: 'projectId',
        label: 'entity.project.project',
        gsResource: this.projectsResource,
        displayExpr: 'name',
        getSegmentLabel: item => `${item['number'] ?? ''} - ${item['name'] ?? ''}`,
      },
      {
        paramKey: 'projectBudgetDistributionId',
        label: 'entity.projectBudgetDistribution.projectBudgetDistribution',
        displayExpr: 'name',
        getSegmentLabel: item => `${item['code'] ?? ''} - ${item['name'] ?? ''}`,
        resolveResource: path => {
          const projectId = path.find(segment => segment.paramKey === 'projectId')?.id;
          if (projectId == null) {
            return undefined;
          }
          return this.api.resources.operations.projectBudgetDistributions.contextualizeQueryParams({ projectId: String(projectId) });
        },
      },
    ];
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onBreadcrumbChange(selection: LevelBreadcrumbSelectionMap): void {
    this.levelParams = selection;
    this.refreshList();
  }

  refreshList(): void {
    void this.cardList?.refresh(this.buildListParams());
  }

  buildListParams(): Record<string, string> {
    const params: Record<string, unknown> = { ...this.levelParams };

    if (this.form) {
      const v = this.form.value;
      params['purchaseOrder'] = v.purchaseOrder;
      params['deliveryNote'] = v.deliveryNote;
      params['productId'] = v.productId;

      const orderStates = v.orderStates as string[];
      params['orderStates'] = orderStates;      
      const lineStates = v.lineStates as number[];
      params['lineStates'] = lineStates;
      
      params['startDate'] = v.startDate;
      params['endDate'] = v.endDate;
      params['startDeliveryDate'] = v.startDeliveryDate;
      params['endDeliveryDate'] = v.endDeliveryDate;
      params['providerId'] = v.providerId;
    }

    return this.normalizeParams(params);
  }

  normalizeParams<T extends Record<string, unknown>>(params: T): Record<string, string> {
    return Object.fromEntries(
      Object.entries(params)
        .filter(([, v]) => v !== undefined && v !== null && v !== '')
        .map(([k, v]) => [
          k,
          v instanceof Date
            ? v.toISOString()
            : Array.isArray(v)
              ? v.map(i => (i instanceof Date ? i.toISOString() : String(i))).join(',')
              : String(v),
        ])
    );
  }

  openPurchaseOrder(line: HomePurchaseOrderLine): void {
    if (line.purchaseOrderId == null) {
      return;
    }

    this.tabs.createTab(OmsPurchaseOrdersEditComponent, TabRole.EDITION, {
      inputPrms: {
        id: line.purchaseOrderId,
      },
    });
  }

  async loadOrderStates(): Promise<void> {
    this.orderStates = [];
    const documentTypes = await firstValueFrom(this.api.resources.operations.documentTypes.contextualizeQueryParams({ natureId: EDocumentTypeNature.PurchaseOrder.toString() }).getAll());
    for (const documentType of documentTypes) {
      const states = await firstValueFrom(this.api.resources.operations.documentTypeStates(documentType).getAll());
      for (const state of states) {
        if (!this.orderStates.find(s => s.name === state.name)) {
          this.orderStates.push({
            id: state.id,
            name: state.name,
          });
        }
      }
    }
  }

  async loadCurrencies(): Promise<void> {
    const currencies = await firstValueFrom(this.api.resources.master.currencies.getAll());
    this.currenciesById = currencies.reduce<Record<string, Currency>>((acc, currency) => {
      acc[String(currency.id)] = currency;
      return acc;
    }, {});
  }

  getCurrencySymbol(line: HomePurchaseOrderLine): string {
    return this.currenciesById[String(line.currencyId)]?.currencySymbol ?? 'EUR';
  }

  getCurrencyDigitsInfo(line: HomePurchaseOrderLine): string {
    const decimals = this.currenciesById[String(line.currencyId)]?.decimals ?? 2;
    return `1.${decimals}-${decimals}`;
  }
}
